import { useEffect, useRef } from "react";
import type { AxiosInstance } from "axios";
import axios from "axios";

import { AppDispatch, RootState } from "../redux/store";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { browserName } from "react-device-detect";
import {
  API_BASE_URL,
  EX_INSTITUTE_PLAN_ECOMMERCE_SUBTYPE_NAME,
  EX_PREMIUM_ECOMMERCE_SUBTYPE_NAME,
  ExtensionStoreLinks,
} from "./constants";
import {
  updateCurrentPlan,
  updateSubscriptions,
} from "../redux/slices/consumerSubscriptionSlice";
import { parsePlanFromKeycloak } from "./isExtensionPremium";
import { useKeycloak } from "@react-keycloak/web";
import { useCookies } from "react-cookie";
import { ROLES } from "./Roles";
import { fetchAllWaPackages } from "../redux/slices/WaPackageSlice";
import { fetchAllTags } from "../redux/slices/TagSlice";

export const useAxios = (
  baseURL: string,
  keycloak?: Keycloak.KeycloakInstance,
) => {
  const axiosInstance = useRef<AxiosInstance>();
  const kcToken = keycloak?.token ?? "";

  useEffect(() => {
    axiosInstance.current = axios.create({
      baseURL,
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
        Authorization: keycloak?.authenticated ? `Bearer ${kcToken}` : "",
      },
      withCredentials: true,
    });

    return () => {
      axiosInstance.current = undefined;
    };
  }, [baseURL, kcToken, keycloak?.authenticated]);

  return axiosInstance;
};

export const useStreamAxios = (baseURL: string) => {
  const { keycloak } = useKeycloak();
  const axiosInstance = useRef<AxiosInstance>();
  const kcToken = keycloak?.token ?? "";

  useEffect(() => {
    axiosInstance.current = axios.create({
      baseURL,
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        Authorization: keycloak?.authenticated ? `Bearer ${kcToken}` : "",
      },
      responseType: "stream",
      adapter: "fetch",
      withCredentials: true,
    });

    return () => {
      axiosInstance.current = undefined;
    };
  }, [baseURL, kcToken, keycloak?.authenticated]);

  return axiosInstance;
};

export const useNonAuthAxios = (baseURL: string) => {
  const axiosInstance = useRef<AxiosInstance>();

  useEffect(() => {
    axiosInstance.current = axios.create({
      baseURL,
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
      },
      withCredentials: true,
    });

    return () => {
      axiosInstance.current = undefined;
    };
  }, [baseURL]);

  return axiosInstance;
};

export const useAppDispatch: () => AppDispatch = useDispatch;

export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export const useBrowserExtensionStore = () => {
  const currentBrowser = browserName;
  let extensionStoreLink = ExtensionStoreLinks[currentBrowser];
  if (!extensionStoreLink) {
    return ExtensionStoreLinks["Chrome"];
  }
  return extensionStoreLink;
};

export const useCESubscription = () => {
  const { initialized, keycloak } = useKeycloak();
  const axios = useAxios(API_BASE_URL, keycloak);
  const dispatch = useAppDispatch();

  const updateUserSubscriptions = (
    success?: () => void,
    failure?: () => void,
  ) => {
    if (initialized && !!keycloak) {
      dispatch(updateCurrentPlan(parsePlanFromKeycloak(keycloak)));

      axios?.current
        ?.post("/subscription")
        .then((r) => {
          dispatch(updateSubscriptions(r?.data));
          keycloak.updateToken(300).then((refreshed) => {
            if (refreshed) {
              dispatch(updateCurrentPlan(parsePlanFromKeycloak(keycloak)));
            }
          });

          !!success && success();
        })
        .catch((e) => !!failure && failure());
    }
  };

  return {
    updateUserSubscriptions,
  };
};

export const useCEUserService = () => {
  const { initialized, keycloak } = useKeycloak();
  const axios = useAxios(API_BASE_URL, keycloak);
  const deleteUser = (success?: () => void, failure?: (e?) => void) => {
    if (initialized && !!keycloak) {
      // dispatch(updateCurrentPlan(parsePlanFromKeycloak(keycloak)));

      axios?.current
        ?.delete("/users/kcusers/user")
        .then((r) => {
          !!success && success();
        })
        .catch((e) => !!failure && failure(e));
    }
  };

  return {
    deleteUser,
  };
};

export const useAnnouncement = () => {
  const [cookies, setCookies, removeCookies] = useCookies(["_anncmntRd"]);
  const newAnnouncementIsReadFlag = cookies["_anncmntRd"];

  const checkAnnouncementIsRead =
    typeof newAnnouncementIsReadFlag !== "undefined" &&
    ((typeof newAnnouncementIsReadFlag === "string" &&
      newAnnouncementIsReadFlag === "true") ||
      (typeof newAnnouncementIsReadFlag === "boolean" &&
        newAnnouncementIsReadFlag));

  const current = new Date();
  const nextYear = new Date();
  nextYear.setFullYear(current.getFullYear() + 1);

  const markNewComingAnnouncementRead = () => {
    setCookies("_anncmntRd", true, { path: "/", expires: nextYear });
  };

  const markNewComingAnnouncementUnRead = () => {
    setCookies("_anncmntRd", false, { path: "/" });
    removeCookies("_anncmntRd", { path: "/" });
  };
  return {
    checkAnnouncementIsRead,
    markNewComingAnnouncementRead,
    markNewComingAnnouncementUnRead,
  };
};

export const useAdmin = () => {
  const { keycloak } = useKeycloak();
  const isAdmin = keycloak?.hasRealmRole(ROLES.ADMIN);
  const isOrgAdmin = keycloak?.hasRealmRole(ROLES.ORG_ADMIN);

  return { isAdmin, isOrgAdmin };
};

export const useSubscriptionRecognize = () => {
  const subs = useAppSelector(
    (state) => state.consumerSubscriptions.subscription?.subscriptions,
  );

  const isInstitutePlan = () => {
    if (Array.isArray(subs)) {
      const planSub = subs.find(
        (s) =>
          s.productSubType.name === EX_INSTITUTE_PLAN_ECOMMERCE_SUBTYPE_NAME,
      );
      return !!planSub;
    }
    return false;
  };

  const isPersonalPremiumPlan = () => {
    if (Array.isArray(subs)) {
      const planSub = subs.find(
        (s) => s.productSubType.name === EX_PREMIUM_ECOMMERCE_SUBTYPE_NAME,
      );
      return !!planSub;
    }
    return false;
  };

  const hasPaidPlan = () => isInstitutePlan() || isPersonalPremiumPlan();

  return { isInstitutePlan, isPersonalPremiumPlan, hasPaidPlan };
};

export const usePaidFeatureAccess = () => {
  const plan = useAppSelector((state) => state.consumerSubscriptions.plan);
  const subscription = useAppSelector(
    (state) => state.consumerSubscriptions.subscription,
  );
  const { hasPaidPlan, isInstitutePlan } = useSubscriptionRecognize();

  const hasActiveSubscription =
    Array.isArray(subscription?.subscriptions) &&
    subscription.subscriptions.length > 0;
  const isFreeForeverPlan = !plan || plan === "Free Forever" || plan === "--";
  const hasPaidAccess =
    hasPaidPlan() || (!isFreeForeverPlan && hasActiveSubscription);

  return {
    hasPaidAccess,
    isFreeUser: !hasPaidAccess,
    isInstitutePlan: isInstitutePlan(),
  };
};

export const useWaPackages = () => {
  const { keycloak } = useKeycloak();
  const dispatch = useAppDispatch();
  const { waPackages, status } = useAppSelector((state) => state.waPackages);

  useEffect(() => {
    if (status === "idle" && keycloak?.authenticated) {
      dispatch(fetchAllWaPackages({ keycloak }));
    }
  }, [status, keycloak, dispatch]);

  return waPackages;
};

export const useTags = () => {
  const { keycloak } = useKeycloak();
  const dispatch = useAppDispatch();
  const { tags, status } = useAppSelector((state) => state.tags);

  useEffect(() => {
    if (status === "idle" && keycloak?.authenticated) {
      dispatch(fetchAllTags({ keycloak }));
    }
  }, [status, keycloak, dispatch]);

  return tags;
};
